/**
 * Weekly review workflow (docs/04 §5).
 * Posted transactions in the last 7 days, grouped by card.
 * Weekly payment suggestions are cash management — never the contractual obligation.
 */

import { asCents, type Cents } from './money';
import { addDays, businessDate, compareBusinessDates, type BusinessDate } from './time';
import type { GraceStatus } from './grace';

/** Exact header text required by docs/04 §5. */
export const WEEKLY_REVIEW_FRAMING =
  'Weekly payments are optional cash management. Your contractual obligation is the statement balance by the due date.';

const PURCHASE_KINDS = new Set(['purchase']);
const CREDIT_KINDS = new Set(['refund', 'statement_credit']);
const SCHEDULED_PAYMENT_STATUSES = new Set(['planned', 'scheduled']);

export type WeeklyReviewTransactionInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  kind: string;
  status: string;
  amount_cents: Cents;
  posted_date: string | null;
};

export type WeeklyReviewPaymentInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  status: string;
  amount_cents: Cents;
};

export type WeeklyReviewCashReservationInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  status: string;
  amount_reserved_cents: Cents;
  transaction_id: string | null;
};

export type WeeklyReviewCardMeta = {
  card_id: string;
  label: string;
  /** Contractual obligation from computeCardObligation — displayed, not suggested as weekly. */
  due_now_cents: Cents;
  next_due_date: BusinessDate | null;
  grace_status: GraceStatus;
  grace_reason: string;
};

export type WeeklyReviewTxnRow = {
  id: string;
  kind: string;
  amount_cents: Cents;
  posted_date: BusinessDate;
  /** True when a reserved cash_reservation already points at this txn. */
  already_reserved: boolean;
};

export type WeeklyReviewCardRow = {
  card_id: string;
  label: string;
  purchases_cents: Cents;
  refunds_credits_cents: Cents;
  net_new_spend_cents: Cents;
  due_now_cents: Cents;
  payments_already_scheduled_cents: Cents;
  /**
   * Cash-management suggestion: week's net spend minus amounts already reserved
   * against this week's transactions. Never equals due_now by construction.
   */
  suggested_additional_payment_cents: Cents;
  /** Same as suggested — cash that still needs reserving for the week's activity. */
  checking_reserve_required_cents: Cents;
  next_due_date: BusinessDate | null;
  grace_status: GraceStatus;
  grace_reason: string;
  transactions: readonly WeeklyReviewTxnRow[];
};

export type WeeklyPlanPayment = {
  card_id: string;
  transaction_ids: readonly string[];
  amount_cents: Cents;
};

export type WeeklyReview = {
  framing: typeof WEEKLY_REVIEW_FRAMING;
  window_start: BusinessDate;
  window_end: BusinessDate;
  cards: readonly WeeklyReviewCardRow[];
  /** Net of selected week transactions → planned payment amount (docs/04 §5 Actions). */
  planPaymentForSelection: (
    cardId: string,
    transactionIds: readonly string[],
  ) => WeeklyPlanPayment | null;
};

export type BuildWeeklyReviewArgs = {
  asOf: BusinessDate;
  cards: readonly WeeklyReviewCardMeta[];
  transactions: readonly WeeklyReviewTransactionInput[];
  payments: readonly WeeklyReviewPaymentInput[];
  reservations: readonly WeeklyReviewCashReservationInput[];
};

function inWindow(posted: string, start: BusinessDate, end: BusinessDate): boolean {
  const d = businessDate(posted);
  return compareBusinessDates(d, start) >= 0 && compareBusinessDates(d, end) <= 0;
}

/**
 * Last 7 calendar days inclusive of asOf: [asOf − 6, asOf].
 */
export function buildWeeklyReview(args: BuildWeeklyReviewArgs): WeeklyReview {
  const windowEnd = args.asOf;
  const windowStart = addDays(args.asOf, -6);

  const reservedTxnIds = new Set<string>();
  const reservedByTxn = new Map<string, Cents>();
  for (const r of args.reservations) {
    if (r.deleted_at !== null) continue;
    if (r.status !== 'reserved') continue;
    if (r.transaction_id === null) continue;
    reservedTxnIds.add(r.transaction_id);
    const prev = reservedByTxn.get(r.transaction_id) ?? asCents(0n);
    reservedByTxn.set(r.transaction_id, asCents(prev + r.amount_reserved_cents));
  }

  const rows: WeeklyReviewCardRow[] = args.cards.map((meta) => {
    const weekTxns: WeeklyReviewTxnRow[] = [];
    let purchases = 0n;
    let credits = 0n;

    for (const t of args.transactions) {
      if (t.deleted_at !== null) continue;
      if (t.card_id !== meta.card_id) continue;
      if (t.status !== 'posted') continue;
      if (t.posted_date === null) continue;
      if (!inWindow(t.posted_date, windowStart, windowEnd)) continue;
      if (!PURCHASE_KINDS.has(t.kind) && !CREDIT_KINDS.has(t.kind)) continue;

      const posted = businessDate(t.posted_date);
      const already = reservedTxnIds.has(t.id);
      weekTxns.push({
        id: t.id,
        kind: t.kind,
        amount_cents: t.amount_cents,
        posted_date: posted,
        already_reserved: already,
      });
      if (PURCHASE_KINDS.has(t.kind)) purchases += t.amount_cents;
      else credits += t.amount_cents;
    }

    weekTxns.sort((a, b) => compareBusinessDates(a.posted_date, b.posted_date));

    let scheduled = 0n;
    for (const p of args.payments) {
      if (p.deleted_at !== null) continue;
      if (p.card_id !== meta.card_id) continue;
      if (!SCHEDULED_PAYMENT_STATUSES.has(p.status)) continue;
      scheduled += p.amount_cents;
    }

    const net = purchases - credits;
    let alreadyReservedAgainstWeek = 0n;
    for (const row of weekTxns) {
      const r = reservedByTxn.get(row.id);
      if (r !== undefined) alreadyReservedAgainstWeek += r;
    }
    const suggestedRaw = net - alreadyReservedAgainstWeek;
    const suggested = asCents(suggestedRaw > 0n ? suggestedRaw : 0n);

    return {
      card_id: meta.card_id,
      label: meta.label,
      purchases_cents: asCents(purchases),
      refunds_credits_cents: asCents(credits),
      net_new_spend_cents: asCents(net),
      due_now_cents: meta.due_now_cents,
      payments_already_scheduled_cents: asCents(scheduled),
      suggested_additional_payment_cents: suggested,
      checking_reserve_required_cents: suggested,
      next_due_date: meta.next_due_date,
      grace_status: meta.grace_status,
      grace_reason: meta.grace_reason,
      transactions: weekTxns,
    };
  });

  function planPaymentForSelection(
    cardId: string,
    transactionIds: readonly string[],
  ): WeeklyPlanPayment | null {
    const card = rows.find((c) => c.card_id === cardId);
    if (card === undefined) return null;
    const idSet = new Set(transactionIds);
    let purchases = 0n;
    let credits = 0n;
    const selected: string[] = [];
    for (const t of card.transactions) {
      if (!idSet.has(t.id)) continue;
      selected.push(t.id);
      if (PURCHASE_KINDS.has(t.kind)) purchases += t.amount_cents;
      else if (CREDIT_KINDS.has(t.kind)) credits += t.amount_cents;
    }
    if (selected.length === 0) return null;
    const net = purchases - credits;
    if (net <= 0n) return null;
    return {
      card_id: cardId,
      transaction_ids: selected,
      amount_cents: asCents(net),
    };
  }

  return {
    framing: WEEKLY_REVIEW_FRAMING,
    window_start: windowStart,
    window_end: windowEnd,
    cards: rows,
    planPaymentForSelection,
  };
}
